// Service for handling file operations (save/load)
import { figureService } from './figureService';
import { termsStore } from '../stores/termsStore';
import { versionStore } from '../stores/versionStore';
import { documentMetadataStore } from '../stores/documentMetadataStore';
import { settingsStore } from '../stores/settingsStore';
import { encryptData, decryptData } from '../utils/cryptoUtils';
import { wordImportService } from './wordImportService';
import { writable, get } from 'svelte/store';

// Save status store for UI feedback
export const saveStatusStore = writable('idle'); // 'idle' | 'saving' | 'saved' | 'error'

// Autosave Service
class AutosaveService {
    constructor() {
        this.fileHandle = null;
        this.autosaveInterval = 60000; // 1 minute
        this.autosaveTimer = null;
        this.lastSavedContent = {
            spec: '',
            claims: '',
            terms: null
        };
        this.isSaving = false;
        this.hasUnsavedChanges = false;
    }

    // Set the file handle after first manual save
    setFileHandle(handle) {
        console.log('AutosaveService: Setting file handle', { 
            hasHandle: !!handle,
            handleType: handle?.constructor?.name,
            wasAlreadySet: !!this.fileHandle
        });
        this.fileHandle = handle;
        this.startAutosave();
    }

    // Clear file handle (e.g., when creating new document)
    clearFileHandle() {
        this.stopAutosave();
        this.fileHandle = null;
        this.lastSavedContent = {
            spec: '',
            claims: '',
            terms: null
        };
        this.hasUnsavedChanges = false;
    }

    // Start autosave timer
    startAutosave() {
        this.stopAutosave(); // Clear any existing timer
        if (this.fileHandle) {
            console.log('AutosaveService: Starting autosave timer with interval:', this.autosaveInterval);
            this.autosaveTimer = setInterval(() => {
                this.performAutosave();
            }, this.autosaveInterval);
        } else {
            console.log('AutosaveService: Cannot start autosave - no file handle');
        }
    }

    // Stop autosave timer
    stopAutosave() {
        if (this.autosaveTimer) {
            clearInterval(this.autosaveTimer);
            this.autosaveTimer = null;
        }
    }

    // Check if content has changed
    hasChanges(specContent, claimsContent, terms) {
        const termsChanged = JSON.stringify(terms) !== JSON.stringify(this.lastSavedContent.terms);
        return specContent !== this.lastSavedContent.spec ||
               claimsContent !== this.lastSavedContent.claims ||
               termsChanged;
    }

    // Perform autosave
    async performAutosave(specContent = null, claimsContent = null) {
        if (!this.fileHandle || this.isSaving) return false;

        // If content not provided, get from stores (for timer-based autosave)
        if (specContent === null || claimsContent === null) {
            // This will be called from the component that has access to editor content
            return false;
        }

        // Get current terms
        let terms = {};
        const unsubscribeTerms = termsStore.subscribe(currentTerms => {
            terms = {
                termEntries: currentTerms.termEntries ? Array.from(currentTerms.termEntries.entries()) : [],
                selectedEntries: currentTerms.selectedEntries ? Array.from(currentTerms.selectedEntries) : [],
                highlightedEntries: currentTerms.highlightedEntries ? Array.from(currentTerms.highlightedEntries) : [],
                expandedEntries: currentTerms.expandedEntries ? Array.from(currentTerms.expandedEntries) : [],
                manualTerms: currentTerms.manualTerms ? Array.from(currentTerms.manualTerms) : [],
                preservedTerms: currentTerms.preservedTerms ? Array.from(currentTerms.preservedTerms) : [],
                sortMode: currentTerms.sortMode || 'appearance'
            };
        });
        unsubscribeTerms();

        // Check if content has changed
        if (!this.hasChanges(specContent, claimsContent, terms)) {
            return false;
        }

        this.isSaving = true;
        saveStatusStore.set('saving');

        try {
            // Use requestIdleCallback for non-blocking save
            await new Promise((resolve, reject) => {
                const performSave = async () => {
                    try {
                        // Get patent type and jurisdiction from settings
                        const settings = get(settingsStore);
                        const patentType = settings.patentType || 'provisional';
                        const jurisdiction = patentType === 'national-phase' ? (settings.jurisdiction || 'US') : null;
                        
                        // Prepare file content
                        const fileContent = {
                            version: "2.0",
                            spec: specContent,
                            claims: claimsContent,
                            figureReferences: figureService.extractFigureReferences(specContent),
                            terms: terms,
                            versionHistory: versionStore.getVersions(),
                            patentType: patentType,
                            jurisdiction: jurisdiction,
                            timestamp: new Date().toISOString()
                        };

                        // Encrypt the data
                        const encryptedData = await encryptData(fileContent);
                        const blob = new Blob([encryptedData], { type: 'application/octet-stream' });

                        // Write to file
                        const writable = await this.fileHandle.createWritable();
                        await writable.write(blob);
                        await writable.close();

                        // Update last saved content
                        this.lastSavedContent = {
                            spec: specContent,
                            claims: claimsContent,
                            terms: terms
                        };
                        this.hasUnsavedChanges = false;

                        saveStatusStore.set('saved');
                        
                        // Auto-hide saved status after 2 seconds
                        setTimeout(() => {
                            const currentStatus = get(saveStatusStore);
                            if (currentStatus === 'saved') {
                                saveStatusStore.set('idle');
                            }
                        }, 2000);

                        resolve();
                    } catch (error) {
                        reject(error);
                    }
                };

                // Use requestIdleCallback if available, otherwise immediate
                if ('requestIdleCallback' in window) {
                    requestIdleCallback(() => performSave());
                } else {
                    performSave();
                }
            });

            return true;
        } catch (error) {
            console.error('Autosave error:', error);
            
            // Handle permission loss
            if (error.name === 'NotAllowedError' || error.name === 'SecurityError') {
                this.fileHandle = null;
                this.stopAutosave();
                saveStatusStore.set('error');
                // Will need to prompt for manual save
                return false;
            }
            
            saveStatusStore.set('error');
            return false;
        } finally {
            this.isSaving = false;
        }
    }

    // Mark content as changed (for UI indicators)
    markAsChanged() {
        this.hasUnsavedChanges = true;
    }
}

// Create singleton instance
export const autosaveService = new AutosaveService();

export const fileService = {
    async saveToFile(specContent, claimsContent, forceDialog = false) {
        console.log('Saving file with claims:', { 
            claimsLength: claimsContent?.length || 0,
            claimsPreview: claimsContent?.substring(0, 100) || 'NO_CLAIMS',
            hasFileHandle: !!autosaveService.fileHandle,
            forceDialog
        });
        
        // Get current terms from the store
        let terms = {};
        const unsubscribeTerms = termsStore.subscribe(currentTerms => {
            // Convert Maps and Sets to serializable arrays
            terms = {
                termEntries: currentTerms.termEntries ? Array.from(currentTerms.termEntries.entries()) : [],
                selectedEntries: currentTerms.selectedEntries ? Array.from(currentTerms.selectedEntries) : [],
                highlightedEntries: currentTerms.highlightedEntries ? Array.from(currentTerms.highlightedEntries) : [],
                expandedEntries: currentTerms.expandedEntries ? Array.from(currentTerms.expandedEntries) : [],
                manualTerms: currentTerms.manualTerms ? Array.from(currentTerms.manualTerms) : [],
                preservedTerms: currentTerms.preservedTerms ? Array.from(currentTerms.preservedTerms) : [],
                sortMode: currentTerms.sortMode || 'appearance'
            };
        });
        unsubscribeTerms();

        // Get version history from localStorage
        const versions = versionStore.getVersions();

        // Extract figure references from spec content
        const figureReferences = figureService.extractFigureReferences(specContent);

        // Get patent type and jurisdiction from settings
        const settings = get(settingsStore);
        const patentType = settings.patentType || 'provisional';
        const jurisdiction = patentType === 'national-phase' ? (settings.jurisdiction || 'US') : null;

        const fileContent = {
            version: "2.0", // Version 2.0 - figures are now only references
            spec: specContent,
            claims: claimsContent,
            figureReferences: figureReferences, // Only save references, not actual figure data
            terms: terms,
            versionHistory: versions,
            patentType: patentType,
            jurisdiction: jurisdiction,
            timestamp: new Date().toISOString()
        };

        try {
            // Encrypt the data with the application key
            const encryptedData = await encryptData(fileContent);
            
            // Create blob from encrypted data
            const blob = new Blob([encryptedData], { type: 'application/octet-stream' });
            
            let handle = null;
            
            // Try to use existing file handle if we have one and not forcing dialog
            if (autosaveService.fileHandle && !forceDialog) {
                handle = autosaveService.fileHandle;
                console.log('Using existing file handle for save');
            } else {
                // Get the document title for the filename
                let suggestedFilename = 'untitled.pdft';
                const unsubscribeMetadata = documentMetadataStore.subscribe(metadata => {
                    if (metadata.title && metadata.title !== 'Untitled document') {
                        // Replace spaces with hyphens and ensure lowercase
                        suggestedFilename = metadata.title.toLowerCase().replace(/\s+/g, '-') + '.pdft';
                    }
                });
                unsubscribeMetadata();
                
                // Show save dialog to get new file handle
                console.log('Showing save dialog');
                try {
                    handle = await window.showSaveFilePicker({
                        suggestedName: suggestedFilename,
                        types: [{
                            description: 'PatDraft File',
                            accept: { 'application/pdft': ['.pdft'] }
                        }]
                    });
                } catch (err) {
                    // User cancelled the save dialog
                    if (err.name === 'AbortError') {
                        console.log('Save cancelled by user');
                        return null;
                    }
                    throw err; // Re-throw other errors to be caught by outer try-catch
                }
            }
            
            // Write to the file
            try {
                const writable = await handle.createWritable();
                await writable.write(blob);
                await writable.close();
                console.log('File saved successfully');
                
                // Store/update the file handle for future saves
                autosaveService.setFileHandle(handle);
                
                // Update last saved content for autosave tracking
                autosaveService.lastSavedContent = {
                    spec: specContent,
                    claims: claimsContent,
                    terms: terms
                };
                
                return handle; // Return handle for caller to know save succeeded
            } catch (writeError) {
                console.error('Failed to write to file:', writeError);
                
                // If write failed and we were using existing handle, it might be stale
                if (!forceDialog && autosaveService.fileHandle) {
                    console.log('Write failed with existing handle, clearing and retrying with dialog');
                    autosaveService.clearFileHandle();
                    // Retry with dialog
                    return this.saveToFile(specContent, claimsContent, true);
                }
                
                throw writeError;
            }
        } catch (error) {
            console.error('Error saving file:', error);
            alert('Failed to save file: ' + error.message);
            return null;
        }
    },

    // Quick save using existing file handle or falling back to save dialog
    async quickSave(specContent, claimsContent) {
        console.log('Quick save requested, file handle exists:', !!autosaveService.fileHandle);
        
        // Use saveToFile which now handles file handle reuse automatically
        // Don't force dialog - let it use existing handle if available
        const handle = await this.saveToFile(specContent, claimsContent, false);
        return handle !== null;
    },

    async loadFromFile() {
        return new Promise((resolve, reject) => {
            const input = document.createElement('input');
            input.type = 'file';
            input.accept = '.pdft';
            
            input.onchange = async (e) => {
                const file = e.target.files[0];
                if (!file) {
                    reject(new Error('No file selected'));
                    return;
                }

                try {
                    // Read file as ArrayBuffer for decryption
                    const arrayBuffer = await file.arrayBuffer();
                    
                    // Decrypt the file with the application key
                    const content = await decryptData(arrayBuffer);
                    
                    console.log('Parsed content structure:', {
                        hasSpec: !!content.spec,
                        hasClaims: !!content.claims,
                        figureReferencesCount: content.figureReferences?.length,
                        version: content.version
                    });
                    console.log('Loaded claims content:', {
                        type: typeof content.claims,
                        length: content.claims?.length || 0,
                        preview: content.claims?.substring(0, 200) || 'NO_CLAIMS'
                    });
                    
                    // Validate the file format
                    if (typeof content.spec !== 'string' || typeof content.claims !== 'string') {
                        console.error('Invalid content types:', { 
                            specType: typeof content.spec, 
                            claimsType: typeof content.claims 
                        });
                        throw new Error('Invalid file format: spec and claims must be strings');
                    }

                    // Validate figure references if present
                    if (content.figureReferences && content.figureReferences.length > 0) {
                        console.log('Validating figure references:', content.figureReferences);
                        
                        // Check if all referenced figures exist in Patdraw
                        const availableFigures = await figureService.fetchFigures();
                        const availableFigureIds = availableFigures.map(f => f.id);
                        
                        const missingFigures = content.figureReferences.filter(
                            refId => !availableFigureIds.includes(refId)
                        );
                        
                        if (missingFigures.length > 0) {
                            console.warn('Some figure references are missing in Patdraw:', missingFigures);
                            // We'll still load the file but warn about missing figures
                        }
                    }


                    // Don't restore terms here - they should be loaded AFTER claims content is set
                    // Store terms for later loading
                    content.termsToLoad = content.terms;

                    // Restore patent type and jurisdiction if present
                    if (content.patentType) {
                        console.log('Restoring patent type from file:', content.patentType);
                        console.log('Restoring jurisdiction from file:', content.jurisdiction || 'US (default)');
                        settingsStore.update(settings => {
                            const newSettings = {
                                ...settings,
                                patentType: content.patentType,
                                jurisdiction: content.jurisdiction || 'US'
                            };
                            console.log('[FileService] Updated settings store:', newSettings);
                            return newSettings;
                        });
                    } else {
                        console.log('No patent type found in file, using defaults');
                        // Set defaults if not present (for older files)
                        settingsStore.update(settings => {
                            const newSettings = {
                                ...settings,
                                patentType: 'provisional',
                                jurisdiction: 'US'
                            };
                            console.log('[FileService] Set default settings:', newSettings);
                            return newSettings;
                        });
                    }

                    // Restore version history if present
                    if (content.versionHistory && Array.isArray(content.versionHistory)) {
                        console.log('Loading version history from file:', {
                            count: content.versionHistory.length,
                            versions: content.versionHistory.map(v => ({ id: v.id, name: v.name, createdAt: v.createdAt }))
                        });
                        
                        // Clear existing versions in localStorage
                        localStorage.removeItem('patdraft_versions');
                        
                        // Save the loaded versions to localStorage
                        localStorage.setItem('patdraft_versions', JSON.stringify(content.versionHistory));
                        
                        // Reload versions in the store
                        versionStore.loadFromStorage();
                        
                        console.log('Version history restored from file');
                    } else {
                        console.log('No version history found in file');
                    }
                    
                    // Clear autosave since we loaded a different file
                    autosaveService.clearFileHandle();
                    
                    resolve(content);
                } catch (error) {
                    console.error('Error loading file:', error);
                    if (error.message.includes('Invalid file format')) {
                        alert('This file is not a valid PDFT file. Please select a file saved from Patdraft.');
                    } else if (error.message.includes('Failed to decrypt')) {
                        alert(error.message);
                    } else {
                        alert('Failed to load file: ' + error.message);
                    }
                    reject(error);
                }
            };

            input.click();
        });
    },

    async importFromWordDocument() {
        return new Promise((resolve, reject) => {
            const input = document.createElement('input');
            input.type = 'file';
            input.accept = '.docx';
            
            input.onchange = async (e) => {
                const file = e.target.files[0];
                if (!file) {
                    reject(new Error('No file selected'));
                    return;
                }

                try {
                    console.log('Importing Word document:', file.name);
                    
                    // Parse and validate the Word document
                    const importedContent = await wordImportService.importWordDocument(file);
                    
                    console.log('Word document imported successfully:', {
                        hasSpec: !!importedContent.spec,
                        hasClaims: !!importedContent.claims,
                        specLength: importedContent.spec?.length || 0,
                        claimsLength: importedContent.claims?.length || 0,
                        originalSections: importedContent.metadata?.originalSections || []
                    });
                    
                    // Note: Word import only updates spec and claims content.
                    // All other stores (terms, versions, metadata, etc.) remain at default values.
                    
                    resolve(importedContent);
                } catch (error) {
                    console.error('Error importing Word document:', error);
                    
                    // Provide user-friendly error messages
                    let userMessage = 'Failed to import Word document: ';
                    if (error.message.includes('Missing required sections')) {
                        userMessage += 'The document is missing required patent sections. ' + error.message;
                    } else if (error.message.includes('unexpected sections')) {
                        userMessage += 'The document contains unexpected sections. ' + error.message;
                    } else if (error.message.includes('empty or missing content')) {
                        userMessage += 'Some sections are empty. ' + error.message;
                    } else {
                        userMessage += error.message;
                    }
                    
                    alert(userMessage);
                    reject(error);
                }
            };

            input.click();
        });
    }
};